import { z } from "zod";
/**
 * Attempt Schema for DynamoDB
 *
 * Attempts are stored with denormalized exercise/module/course info
 * to enable efficient queries without joins.
 *
 * PK: USER#<uid>
 * SK: ATTEMPT#<exerciseId>#<timestamp>
 */
// Base Attempt schema for DynamoDB
// Matches server AttemptRepository.itemToAttempt() output
export const AttemptSchema = z.object({
    id: z.number().int(),
    exercise_id: z.number().int(),
    user_uid: z.string(), // Cognito UID - primary identifier
    answers: z.unknown().nullable(), // User's answers (primary field)
    structure: z.string().nullable(), // Same data as answers, stringified for UI backward compat
    score: z.number().min(0),
    max_score: z.number().min(0),
    time_spent_seconds: z.number().int().min(0).default(0),
    is_correct: z.boolean().default(false),
    feedback: z.unknown().nullable(),
    created_at: z.date(),
    updated_at: z.date(),
    // Denormalized exercise info (DynamoDB - avoid joins)
    exercise_type: z.string().optional(),
    exercise_name: z.string().optional(),
    // Denormalized module info
    module_id: z.number().int().optional(),
    module_name: z.string().optional(),
    // Denormalized course info
    course_id: z.number().int().optional(),
    course_name: z.string().optional(),
});
// Payload schema for creating/updating attempts
export const AttemptPayloadSchema = z.object({
    exercise_id: z.number().int().optional(),
    user_uid: z.string().optional(),
    structure: z.unknown().optional(), // UI field name for answers
    answers: z.unknown().optional(), // Alternative field name
    score: z.number().min(0).default(0),
    max_score: z.number().min(0).optional(),
    time_spent_seconds: z.number().int().min(0).optional(),
    is_correct: z.boolean().optional(),
    feedback: z.unknown().optional(),
    // Denormalized info for create operations
    exercise_type: z.string().optional(),
    exercise_name: z.string().optional(),
    module_id: z.number().int().optional(),
    module_name: z.string().optional(),
    course_id: z.number().int().optional(),
    course_name: z.string().optional(),
});
// Schema for latest module attempts with exercise details
export const ModuleAttemptSchema = AttemptSchema;
// Schema for module attempts summary
export const ModuleAttemptsSummarySchema = z.object({
    attemptedExercisesCount: z.number().int().min(0),
    totalExercisesCount: z.number().int().min(0),
    totalBestResults: z.number().min(0),
    averageScore: z.number().min(0).max(100),
    completedExercisesCount: z.number().int().min(0),
});
// Schema for exercise attempt statistics
export const ExerciseAttemptStatsSchema = z.object({
    total_attempts: z.number().int().min(0),
    unique_users: z.number().int().min(0),
    average_score: z.number().min(0).max(100),
    completion_rate: z.number().min(0).max(100),
    average_time_spent: z.number().min(0),
});
// Schema for filters used in queries
export const AttemptFiltersSchema = z
    .record(z.string(), z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.date(),
    z.array(z.union([z.string(), z.number(), z.boolean()])),
]))
    .optional();
// Schema for attempt structure data (parsed JSON)
export const AttemptStructureSchema = z
    .object({
    answers: z.array(z.unknown()).optional(),
    responses: z.record(z.string(), z.unknown()).optional(),
    user_input: z.unknown().optional(),
    selections: z.array(z.union([z.string(), z.number()])).optional(),
})
    .passthrough();
// Schema for attempt feedback data
export const AttemptFeedbackSchema = z
    .object({
    correct_answers: z.array(z.unknown()).optional(),
    explanations: z.array(z.string()).optional(),
    hints: z.array(z.string()).optional(),
    overall_feedback: z.string().optional(),
    score_breakdown: z.record(z.string(), z.number()).optional(),
})
    .passthrough();
// Schema for creating an attempt
export const CreateAttemptSchema = z.object({
    exercise_id: z.number().int(),
    user_uid: z.string(),
    answers: z.unknown().optional(),
    structure: AttemptStructureSchema.optional(), // UI backward compat
    score: z.number().min(0).default(0),
    max_score: z.number().min(0).optional(),
    time_spent_seconds: z.number().int().min(0).optional(),
    is_correct: z.boolean().optional(),
    feedback: AttemptFeedbackSchema.optional(),
    // Denormalized info
    exercise_type: z.string().optional(),
    exercise_name: z.string().optional(),
    module_id: z.number().int().optional(),
    module_name: z.string().optional(),
    course_id: z.number().int().optional(),
    course_name: z.string().optional(),
});
// Schema for updating an attempt
export const UpdateAttemptSchema = z.object({
    answers: z.unknown().optional(),
    structure: AttemptStructureSchema.optional(), // UI backward compat
    score: z.number().min(0).optional(),
    max_score: z.number().min(0).optional(),
    time_spent_seconds: z.number().int().min(0).optional(),
    is_correct: z.boolean().optional(),
    feedback: AttemptFeedbackSchema.optional(),
});
// Schema for attempt query parameters
export const AttemptQuerySchema = z.object({
    userUid: z.string().optional(),
    exerciseId: z.number().int().optional(),
    moduleId: z.number().int().optional(),
    limit: z.number().int().min(1).max(100).default(10),
});
// Schema for user best score response
export const UserBestScoreSchema = z.object({
    user_uid: z.string(),
    exercise_id: z.number().int(),
    best_score: z.number().min(0).max(100).nullable(),
});
// Schema for attempt completion tracking
export const AttemptCompletionTrackingSchema = z.object({
    attempt_id: z.number().int(),
    user_uid: z.string(),
    exercise_id: z.number().int(),
    score: z.number().min(0).nullable(),
    is_correct: z.boolean(),
    completion_threshold: z.number().min(0).max(100),
    time_spent_seconds: z.number().int().min(0),
});
